// Base attachment types for notes (original 13 types)
export const ALLOWED_NOTE_ATTACHMENT_TYPES = [
  // Images
  'jpg',
  'jpeg',
  'png',
  'gif',
  'svg',
  'webp',
  // Documents
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
] as const

// Extended attachment types for emails (includes notes + additional types)
export const ALLOWED_EMAIL_ATTACHMENT_TYPES = [
  ...ALLOWED_NOTE_ATTACHMENT_TYPES,
  // Archives
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  // Text files
  'txt',
  'csv',
  'json',
  'xml',
  // Media files
  'mp3',
  'mp4',
  'mov',
  'avi',
] as const

// Email attachments always use multipart/form-data (raw binary, no encoding overhead)
export const MAX_EMAIL_ATTACHMENT_SIZE = 25 * 1024 * 1024 // 25MB (Nylas multipart limit)
export const MAX_NOTE_ATTACHMENT_SIZE = 4 * 1024 * 1024 // 4MB
export const EMAIL_ATTACHMENT_WARNING_THRESHOLD = 20 * 1024 * 1024 // 20MB

export type AllowedNoteAttachmentType =
  (typeof ALLOWED_NOTE_ATTACHMENT_TYPES)[number]
export type AllowedEmailAttachmentType =
  (typeof ALLOWED_EMAIL_ATTACHMENT_TYPES)[number]

export const isAllowedAttachmentType = (
  filename: string,
  allowedTypes: readonly string[]
): boolean => {
  const extension = filename.split('.').pop()?.toLowerCase()
  return extension ? allowedTypes.includes(extension) : false
}

export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || ''
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export const getAcceptString = (types: readonly string[]): string => {
  return types.map((ext) => `.${ext}`).join(',')
}

/** Minimal file shape the attachment validator needs (`File` satisfies it). */
export interface AttachmentFileLike {
  name: string
  size: number
}

/**
 * Pure validation for adding files to an attachment list: allowed extension,
 * per-file size cap, and total-size cap including what is already attached.
 * Returns the user-facing error message on rejection, or `null` when the
 * files fit. Shared by the file-picker path (`useFileAttachments`) and the
 * spec-sheet attach path so both enforce identical limits.
 */
export const getAttachmentAdditionError = (
  files: readonly AttachmentFileLike[],
  limits: {
    allowedTypes: readonly string[]
    maxFileSize: number
    maxTotalSize: number
    /** Combined size of the attachments already in the list. */
    currentTotalSize: number
  }
): string | null => {
  const { allowedTypes, maxFileSize, maxTotalSize, currentTotalSize } = limits

  if (files.some((file) => !isAllowedAttachmentType(file.name, allowedTypes))) {
    return `Only the following file types are allowed: ${allowedTypes.join(', ')}`
  }

  if (files.some((file) => file.size > maxFileSize)) {
    return `The maximum file size allowed is ${formatFileSize(maxFileSize)}`
  }

  const newTotalSize =
    currentTotalSize + files.reduce((sum, file) => sum + file.size, 0)
  if (newTotalSize > maxTotalSize) {
    return `Total attachments size would exceed ${formatFileSize(maxTotalSize)} limit`
  }

  return null
}
